import { api, setToken } from '../../api.ts';
import { agentCounts, agentsApi, byUrgency } from '../../agents-api.ts';
import type { AgentInfo, ProjectInfo } from '../../agents-api.ts';
import { h } from '../../ui.ts';
import { agentRow } from '../agents/row.ts';
import { counts, icon, itemCard, screen } from './kit.ts';
import type { TalkSession, WorkItem } from './kit.ts';
import { projectSwitcher, talkButtonLabel } from './switcher.ts';

const workOf = async (id: string) =>
  (await api.get<{ items: WorkItem[] }>(`/sessions/${id}/work`).catch(() => null))?.items ?? [];

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;
const clock = (ms: number) => new Date(ms).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

interface Tally {
  working: number;
  needs: number;
  ready: number;
}

/** Home (design-docs/0028, 0030): what needs you, the agents, the project to talk about, and one button to talk. */
export async function homeView(): Promise<HTMLElement> {
  const [sessions, agents, projects] = await Promise.all([
    api.get<TalkSession[]>('/sessions'),
    agentsApi.list().catch(() => [] as AgentInfo[]),
    agentsApi.projects().catch(() => [] as ProjectInfo[]),
  ]);
  // talks and chats both file work (design-docs/0028, 0032)
  const talks = sessions
    .filter((s) => s.kind === 'talk' || s.kind === 'chat')
    .sort((a, b) => b.startedAt - a.startedAt);
  const chatOpen = talks.some((s) => s.kind === 'chat' && !s.endedAt);
  const work = (await Promise.all(talks.slice(0, 3).map((s) => workOf(s.id)))).flat();
  const w = counts(work);
  const ac = agentCounts(agents);
  const tally = { working: w.working + ac.running, needs: w.needs + ac.asking, ready: w.ready + ac.previews };
  const last = talks.find((s) => s.endedAt);
  const startLabel = h(
    'span',
    {},
    talkButtonLabel(
      projects,
      talks.some((s) => s.kind === 'talk' && !s.endedAt),
    ),
  );
  return screen(
    header(),
    greeting(tally),
    strip(tally),
    agentsSection(agents),
    work.length
      ? h(
          'section',
          { class: 'p-section', 'aria-label': 'Recent work' },
          h('h2', {}, 'Filed in talks'),
          h('div', { class: 'p-cards' }, ...work.slice(0, 4).map(itemCard)),
        )
      : null,
    last ? await lastTalk(last) : null,
    h('div', { class: 'p-grow' }),
    h(
      'div',
      { class: 'p-foot' },
      projects.length
        ? projectSwitcher(projects, () => (startLabel.textContent = talkButtonLabel(projects, false)))
        : null,
      h('button', { class: 'p-primary', onclick: () => (location.hash = '#/talk') }, icon.mic(20), startLabel),
      h(
        'button',
        { class: 'p-secondary', onclick: () => (location.hash = '#/chat') },
        icon.send(18),
        chatOpen ? 'Back to the chat' : 'Chat instead',
      ),
      h('span', { class: 'p-hint' }, 'Hands-free: she says everything, the screen can stay off.'),
    ),
  );
}

function header(): HTMLElement {
  const signOut = () => {
    setToken('');
    location.reload();
  };
  return h(
    'header',
    { class: 'p-head' },
    h('span', { class: 'p-brand' }, h('span', { class: 'brand-mark' }, icon.mark(14)), 'Assistant'),
    h(
      'div',
      { class: 'row' },
      h('a', { class: 'p-icon', href: '#/projects', 'aria-label': 'Projects' }, icon.folder(18)),
      h('button', { class: 'p-icon', 'aria-label': 'Sign out', onclick: signOut }, icon.out(18)),
    ),
  );
}

function greeting(c: Tally): HTMLElement {
  const now = new Date();
  const hour = now.getHours();
  const hello = hour < 12 ? 'Good morning.' : hour < 18 ? 'Good afternoon.' : 'Good evening.';
  const sub = c.needs
    ? `${plural(c.needs, 'thing')} need${c.needs === 1 ? 's' : ''} you.`
    : c.ready
      ? `${plural(c.ready, 'preview')} ready.`
      : c.working
        ? `${c.working} at work.`
        : 'All quiet.';
  const day = now.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' });
  return h(
    'div',
    { class: 'p-greet' },
    h('span', { class: 'p-mono' }, `${day} · ${clock(now.getTime())}`.toUpperCase()),
    h('h1', { class: 'p-title' }, hello, h('br'), h('span', {}, sub)),
  );
}

function strip(c: Tally): HTMLElement {
  const cell = (tone: string, label: string, n: number, hot = false) =>
    h(
      'div',
      { class: hot ? 'hot' : '' },
      h('span', { class: 'p-label' }, h('span', { class: `dot tone-${tone}` }), label),
      h('b', {}, String(n)),
    );
  return h(
    'section',
    { class: 'p-strip', 'aria-label': 'Work status' },
    cell('accent', 'Working', c.working),
    cell('warning', 'Needs you', c.needs, c.needs > 0),
    cell('success', 'Previews', c.ready),
  );
}

/** The Agents entry: counts, and the most urgent few right here. */
function agentsSection(agents: AgentInfo[]): HTMLElement {
  const c = agentCounts(agents);
  const urgent = [...agents]
    .sort(byUrgency)
    .filter((a) => a.status !== 'stopped')
    .slice(0, 2);
  const sub = agents.length
    ? `${c.running} running · ${c.asking} asking${c.previews ? ` · ${plural(c.previews, 'preview')}` : ''}`
    : 'None yet. Ask her in a talk to build something.';
  return h(
    'section',
    { class: 'p-section', 'aria-label': 'Agents' },
    h(
      'a',
      { class: `p-card p-entry ${c.asking ? 'amber' : ''}`, href: '#/agents' },
      h('span', { class: 'p-entry-icon' }, icon.bot(18)),
      h('span', { class: 'p-text' }, h('span', {}, 'Agents'), h('span', { class: 'p-muted' }, sub)),
      icon.right(16),
    ),
    urgent.length ? h('div', { class: 'agent-rows' }, ...urgent.map((a) => agentRow(a))) : null,
  );
}

async function lastTalk(s: TalkSession): Promise<HTMLElement> {
  const items = await workOf(s.id);
  const mins = Math.max(1, Math.round(((s.endedAt ?? s.startedAt) - s.startedAt) / 60000));
  return h(
    'section',
    { class: 'p-section' },
    h(
      'a',
      { class: 'p-card', href: `#/talk/${s.id}` },
      h(
        'span',
        { class: 'p-text' },
        h('span', { class: 'p-mono' }, `LAST ${s.kind.toUpperCase()} · ${clock(s.startedAt)} · ${mins}M`),
        h('span', {}, `${items.length} filed · ${plural(counts(items).ready, 'preview')}`),
      ),
      icon.right(16),
    ),
  );
}
